import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { isKadryModuleStateError, kadryErrorCode, kadryErrorMessage } from '@entities/kadry';

const Actions = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

interface Props {
  error: unknown;
  /**
   * Перечитать данные с сервера. Для `version_conflict` показывается кнопка
   * «Обновить данные»; без обработчика кнопка перезагружает страницу.
   */
  onRefresh?: () => void;
  /** Скрыть баннер (например, после исправления формы). */
  onDismiss?: () => void;
  /** Заголовок для обычных ошибок (по умолчанию — «Не удалось выполнить»). */
  title?: string;
}

/**
 * Ошибка API модуля Кадры для пользователя. Понимает коды ADR-0019 §8:
 * `version_conflict` — «данные изменились» с кнопкой обновления;
 * `feature_disabled` / `module_unavailable` — баннер состояния модуля или
 * функции; остальное — текст ошибки сервера.
 */
export function KadryErrorBanner({ error, onRefresh, onDismiss, title }: Props) {
  if (!error) return null;
  const code = kadryErrorCode(error);
  const message = kadryErrorMessage(error);
  const refresh = onRefresh ?? (() => window.location.reload());

  if (code === 'version_conflict') {
    return (
      <FallbackBanner tone="warning" title="Данные изменились">
        {message} Пока вы работали, объект изменил другой пользователь или другая вкладка.
        <Actions>
          <Button type="button" $size="sm" onClick={refresh}>
            Обновить данные
          </Button>
          {onDismiss && (
            <Button type="button" $size="sm" $variant="ghost" onClick={onDismiss}>
              Скрыть
            </Button>
          )}
        </Actions>
      </FallbackBanner>
    );
  }

  if (isKadryModuleStateError(error)) {
    return (
      <FallbackBanner
        tone={code === 'module_unavailable' ? 'danger' : 'info'}
        title={code === 'module_unavailable' ? 'Модуль недоступен' : 'Модуль выключен'}
      >
        {code === 'module_unavailable'
          ? message
          : 'ОСНОВА.Кадры включается после приёмки всей матрицы испытаний. До этого модуль работает только на тестовых данных.'}
      </FallbackBanner>
    );
  }

  if (code === 'feature_disabled') {
    return (
      <FallbackBanner tone="info" title="Функция выключена" onDismiss={onDismiss}>
        {message.charAt(0).toUpperCase() + message.slice(1)}. Её включает администратор подбора
        в разделе «Настройки → Функции» после выполнения условия включения.
      </FallbackBanner>
    );
  }

  return (
    <FallbackBanner tone="danger" title={title ?? 'Не удалось выполнить'} onDismiss={onDismiss}>
      {message}
    </FallbackBanner>
  );
}
